import { useState } from 'react';
import { Table, Button, Space, Switch, Progress, Tag, Tooltip, App, theme, type TableColumnsType } from 'antd';
import { PlusOutlined, ThunderboltOutlined, EditOutlined, DeleteOutlined, GlobalOutlined, CheckCircleOutlined, TagOutlined, PercentageOutlined } from '@ant-design/icons';
import { formatDistanceToNow } from 'date-fns';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useLanguages, useUpdateLanguage } from '@/hooks/useLanguages';
import { getLanguagesStats, type LanguageCoverage } from '@/api/languagesStats';
import { AddLanguageModal } from './AddLanguageModal';
import { EditLanguageDrawer } from './EditLanguageDrawer';
import { useConfirmDeleteLanguage } from './DeleteLanguageConfirm';
import { AiTranslateModal } from '@/components/AiTranslateModal';
import { EmptyState } from '@/components/EmptyState';
import { PageHero, HeroChip } from '@/components/PageHero';
import { KpiTile } from '@/components/KpiTile';
import { SectionCard } from '@/components/SectionCard';
import type { Language } from '@/types/language';

export function LanguagesListPage() {
  const { message } = App.useApp();
  const qc = useQueryClient();
  const { token } = theme.useToken();
  const langsQ = useLanguages();
  const langs = langsQ.data ?? [];
  const statsQ = useQuery({ queryKey: ['languages-stats'], queryFn: getLanguagesStats });
  const stats = statsQ.data;
  const covMap = new Map<string, LanguageCoverage>((stats?.coverage ?? []).map((c) => [c.code, c]));
  const totalKeys = stats?.total_ui_keys ?? 0;

  const updateMut = useUpdateLanguage();
  const confirmDel = useConfirmDeleteLanguage();
  const invalidateStats = () => qc.invalidateQueries({ queryKey: ['languages-stats'] });

  const [addOpen, setAddOpen] = useState(false);
  const [editLang, setEditLang] = useState<Language | null>(null);
  const [aiLang, setAiLang] = useState<string | null>(null);

  async function onToggleEnabled(lang: Language, next: boolean) {
    try {
      await updateMut.mutateAsync({ code: lang.code, input: { enabled: next } });
      message.success(`${lang.code} ${next ? 'enabled' : 'disabled'}`);
      invalidateStats();
    } catch (e) {
      message.error((e as Error).message);
    }
  }

  const columns: TableColumnsType<Language> = [
    { title: 'Code', dataIndex: 'code', width: 110, render: (v) => <code>{v}</code> },
    { title: 'Name', dataIndex: 'name_native', ellipsis: true },
    { title: 'Bundled', dataIndex: 'is_bundled', width: 90, render: (v: boolean) => (v ? <Tag color="blue">✓ bundled</Tag> : <span>-</span>) },
    { title: 'Enabled', dataIndex: 'enabled', width: 100, render: (v: boolean, lang) => <Switch checked={v} onChange={(next) => onToggleEnabled(lang, next)} /> },
    {
      title: 'UI coverage', width: 200,
      render: (_, lang) => {
        const cov = covMap.get(lang.code);
        const pct = cov?.pct ?? 0;
        return (
          <Tooltip title={`${cov?.count ?? 0} / ${totalKeys} keys`}>
            <Progress percent={pct} size="small" status={pct === 100 ? 'success' : 'active'} />
          </Tooltip>
        );
      },
    },
    { title: 'Last updated', dataIndex: 'updated_at', width: 160, render: (v: string) => formatDistanceToNow(new Date(v), { addSuffix: true }) },
    {
      title: 'Actions', width: 200,
      render: (_, lang) => (
        <Space>
          <Tooltip title="AI Translate"><Button size="small" icon={<ThunderboltOutlined />} disabled={lang.is_bundled} onClick={() => setAiLang(lang.code)} /></Tooltip>
          <Tooltip title="Edit"><Button size="small" icon={<EditOutlined />} onClick={() => setEditLang(lang)} /></Tooltip>
          <Tooltip title={lang.is_bundled ? 'Bundled languages cannot be deleted' : 'Delete'}>
            <Button size="small" danger icon={<DeleteOutlined />} disabled={lang.is_bundled}
              onClick={async () => { await confirmDel(lang); invalidateStats(); }} />
          </Tooltip>
        </Space>
      ),
    },
  ];

  const Bar = ({ label, pct }: { label: string; pct: number }) => (
    <div style={{ marginBottom: 8 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginBottom: 4 }}>
        <span>{label}</span>
        <span style={{ color: token.colorTextSecondary }}>{pct}%</span>
      </div>
      <div style={{ height: 8, borderRadius: 4, background: token.colorFillSecondary }}>
        <div style={{ height: 8, borderRadius: 4, width: `${pct}%`, background: '#0ea5e9' }} />
      </div>
    </div>
  );

  return (
    <div style={{ maxWidth: 1100 }}>
      <PageHero
        icon={<GlobalOutlined />}
        title="Languages"
        subtitle="UI languages and translation coverage"
        from="#0ea5e9" to="#6366f1"
        chips={[
          <HeroChip key="total">Total {stats?.total ?? 0}</HeroChip>,
          <HeroChip key="enabled">Enabled {stats?.enabled ?? 0}</HeroChip>,
        ]}
        actions={<Button type="primary" icon={<PlusOutlined />} onClick={() => setAddOpen(true)}>Add Language</Button>}
      />

      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 20 }}>
        <KpiTile tone="sky" icon={<GlobalOutlined />} label="Total" value={stats?.total ?? 0} />
        <KpiTile tone="green" icon={<CheckCircleOutlined />} label="Enabled" value={stats?.enabled ?? 0} />
        <KpiTile tone="indigo" icon={<TagOutlined />} label="Bundled" value={stats?.bundled ?? 0} />
        <KpiTile tone="amber" icon={<PercentageOutlined />} label="Avg coverage" value={`${stats?.avg_coverage_pct ?? 0}%`} />
      </div>

      <SectionCard tone="sky" icon={<PercentageOutlined />} title="Coverage" desc="UI-string coverage per language">
        {(stats?.coverage ?? []).map((c) => <Bar key={c.code} label={`${c.name} (${c.code})`} pct={c.pct} />)}
      </SectionCard>

      <SectionCard tone="sky" icon={<GlobalOutlined />} title="All languages" desc="Enable, translate, edit">
        <Table
          rowKey="code"
          loading={langsQ.isLoading}
          columns={columns}
          dataSource={langs}
          pagination={false}
          locale={{
            emptyText: (
              <EmptyState title="No languages yet" description="Add your first language to start translating UI strings." ctaLabel="Add Language" onCta={() => setAddOpen(true)} />
            ),
          }}
        />
      </SectionCard>

      <AddLanguageModal open={addOpen} onClose={() => setAddOpen(false)} onCreated={(code, runAi) => { invalidateStats(); if (runAi) setAiLang(code); }} />
      <EditLanguageDrawer open={!!editLang} language={editLang} onClose={() => setEditLang(null)} />
      {aiLang && <AiTranslateModal open={!!aiLang} defaultTargetLang={aiLang} onClose={() => setAiLang(null)} />}
    </div>
  );
}
